import * as LitComps from "./LitButton";
import { SlotIcon, SlotIconName } from "../Icon";
import { sysColors, extendedColors } from "@/assets/theme/colors";

const variants = {
  outlined: LitComps.LitOutlinedButton,
  outlinedTonal: LitComps.LitOutlinedButton,
  filled: LitComps.LitFilledButton,
  elevated: LitComps.LitElevatedButton,
  filledTonal: LitComps.LitFilledTonalButton,
  text: LitComps.LitTextButton,
  squared: LitComps.LitSquaredButton,
  squaredSmall: LitComps.LitSquaredSmallButton
};

export type ButtonVariant = keyof typeof variants;

const themes = {
  dark: {
    "--md-sys-color-primary": sysColors["sys-brand-inverse-primary"],
    "--md-sys-color-outline": sysColors["sys-brand-outline"]
  } as React.CSSProperties,
  light: {
    // Focus ring customization for all button variants
    "--md-focus-ring-color": sysColors["sys-brand-primary"],
    "--md-sys-color-outline": sysColors["sys-brand-outline"],
    // Specific focus colors for different button types
    "--md-filled-button-focus-outline-color": sysColors["sys-brand-primary"],
    "--md-outlined-button-focus-outline-color": sysColors["sys-brand-primary"],
    "--md-elevated-button-focus-outline-color": sysColors["sys-brand-primary"],
    "--md-filled-tonal-button-focus-outline-color": sysColors["sys-brand-primary"],
    "--md-text-button-focus-outline-color": sysColors["sys-brand-primary"]
  } as React.CSSProperties,
  error: {
    "--md-sys-color-primary": sysColors["sys-brand-error"],
    // Focus ring for error theme
    "--md-focus-ring-color": sysColors["sys-brand-error"],
    "--md-sys-color-outline": sysColors["sys-brand-error"],
    "--md-filled-button-focus-outline-color": sysColors["sys-brand-error"],
    "--md-outlined-button-focus-outline-color": sysColors["sys-brand-error"],
    "--md-elevated-button-focus-outline-color": sysColors["sys-brand-error"],
    "--md-filled-tonal-button-focus-outline-color": sysColors["sys-brand-error"],
    "--md-text-button-focus-outline-color": sysColors["sys-brand-error"],
    // Text colors
    "--md-outlined-button-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-outlined-button-hover-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-outlined-button-focus-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-outlined-button-pressed-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-hover-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-focus-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-pressed-label-text-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-icon-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-hover-icon-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-focus-icon-color": sysColors["sys-brand-on-error-container"],
    "--md-text-button-pressed-icon-color": sysColors["sys-brand-on-error-container"]
  } as React.CSSProperties,
  warn: {
    "--md-sys-color-primary": extendedColors["extended-warning-brand-on-color-container"],
    // Focus ring for warn theme
    "--md-focus-ring-color": extendedColors["extended-warning-brand-on-color-container"],
    "--md-sys-color-outline": extendedColors["extended-warning-brand-on-color-container"],
    "--md-filled-button-focus-outline-color": extendedColors["extended-warning-brand-on-color-container"],
    "--md-outlined-button-focus-outline-color": extendedColors["extended-warning-brand-on-color-container"],
    "--md-elevated-button-focus-outline-color": extendedColors["extended-warning-brand-on-color-container"],
    "--md-filled-tonal-button-focus-outline-color": extendedColors["extended-warning-brand-on-color-container"],
    "--md-text-button-focus-outline-color": extendedColors["extended-warning-brand-on-color-container"]
  } as React.CSSProperties,
  info: {
    "--md-sys-color-primary": sysColors["sys-brand-on-primary-container"],
    // Focus ring for info theme
    "--md-focus-ring-color": sysColors["sys-brand-on-primary-container"],
    "--md-sys-color-outline": sysColors["sys-brand-on-primary-container"],
    "--md-filled-button-focus-outline-color": sysColors["sys-brand-on-primary-container"],
    "--md-outlined-button-focus-outline-color": sysColors["sys-brand-on-primary-container"],
    "--md-elevated-button-focus-outline-color": sysColors["sys-brand-on-primary-container"],
    "--md-filled-tonal-button-focus-outline-color": sysColors["sys-brand-on-primary-container"],
    "--md-text-button-focus-outline-color": sysColors["sys-brand-on-primary-container"]
  } as React.CSSProperties,
  pgaAssistant: {
    "--md-sys-color-primary": sysColors["sys-brand-primary-container-bright"],
    "--md-sys-color-on-primary": sysColors["sys-brand-on-primary-container"],
    // Focus ring for pgaAssistant theme
    "--md-focus-ring-color": sysColors["sys-brand-primary-container-bright"],
    "--md-sys-color-outline": sysColors["sys-brand-primary-container-bright"],
    "--md-filled-button-focus-outline-color": sysColors["sys-brand-primary-container-bright"],
    "--md-outlined-button-focus-outline-color": sysColors["sys-brand-primary-container-bright"],
    "--md-elevated-button-focus-outline-color": sysColors["sys-brand-primary-container-bright"],
    "--md-filled-tonal-button-focus-outline-color": sysColors["sys-brand-primary-container-bright"],
    "--md-text-button-focus-outline-color": sysColors["sys-brand-primary-container-bright"]
  } as React.CSSProperties
};

export type ThemeName = keyof typeof themes;

const filledWarnFill = {
  "--md-sys-color-primary": extendedColors["extended-warning-brand-color"],
  // Amber chips use white on-color in the palette, but that fails contrast on #FFBB4B.
  "--md-sys-color-on-primary": extendedColors["extended-warning-brand-on-color-container"]
} as React.CSSProperties;

const outlinedTonalStyles = {
  "--md-outlined-button-outline-color": sysColors["sys-brand-outline-variant"],
  "--md-outlined-button-pressed-outline-color": sysColors["sys-brand-outline-variant"],
  "--md-outlined-button-label-text-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-hover-label-text-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-focus-label-text-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-pressed-label-text-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-icon-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-hover-icon-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-focus-icon-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-pressed-icon-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-hover-state-layer-color": sysColors["sys-brand-on-surface-variant"],
  "--md-outlined-button-pressed-state-layer-color": sysColors["sys-brand-on-surface-variant"]
} as React.CSSProperties;

export interface Props {
  disabled?: boolean;
  icon?: SlotIconName;
  svgIcon?: React.ReactNode;
  trailingIcon?: boolean;
  variant?: ButtonVariant;
  testId?: string;
  /** Explicit accessible name for analytics and screen readers. */
  ariaLabel?: string;
  /** Accordion / disclosure expanded state. */
  ariaExpanded?: boolean;
  /** Toggle pressed state for selectable buttons. */
  ariaPressed?: boolean;
  /** WAI-ARIA keyboard shortcut hint (e.g. `Control+D Meta+D`). */
  ariaKeyShortcuts?: string;
  onClick?(): void;
  theme?: ThemeName;
  children?: React.ReactNode;
  iconClassName?: string;
  iconSlotClassName?: string;
  iconWeight?: 300 | 400 | 800;
  type?: "button" | "submit" | "reset";
  style?: React.CSSProperties;
}

export default function Button({
  children,
  disabled,
  icon,
  svgIcon,
  trailingIcon,
  variant = "filled",
  onClick,
  testId,
  ariaLabel,
  ariaExpanded,
  ariaPressed,
  ariaKeyShortcuts,
  theme = "light",
  iconClassName,
  iconSlotClassName,
  iconWeight,
  type,
  style: styleProp
}: Props) {
  const ButtonComponent = variants[variant];
  const style = {
    ...themes[theme],
    ...(variant === "filled" && theme === "warn" ? filledWarnFill : {}),
    ...(variant === "outlinedTonal" ? outlinedTonalStyles : {}),
    ...styleProp
  };
  let iconPixelSize = 18;
  if (variant === "squared" || variant === "squaredSmall") {
    iconPixelSize = 16;
  } else if (iconWeight !== undefined) {
    iconPixelSize = 24;
  }

  return (
    <ButtonComponent
      disabled={disabled}
      onClick={onClick}
      trailingIcon={trailingIcon}
      data-testid={testId}
      style={style}
      type={type}
      {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      {...(ariaExpanded != null ? { "aria-expanded": ariaExpanded } : {})}
      {...(ariaPressed != null ? { "aria-pressed": ariaPressed } : {})}
      {...(ariaKeyShortcuts ? { "aria-keyshortcuts": ariaKeyShortcuts } : {})}
    >
      {svgIcon
        ? svgIcon
        : icon && (
            <SlotIcon
              name={icon}
              slot="icon"
              disabled={disabled}
              className={iconClassName}
              slotWrapperClassName={iconSlotClassName}
              size={iconPixelSize}
              weight={iconWeight}
            />
          )}
      {children}
    </ButtonComponent>
  );
}
